import { differenceInCalendarDays, format, isSameDay, isSameYear } from "date-fns";
import { ru, enUS, uk, fr, uz } from "date-fns/locale";
import { useLocale } from "../Providers/i18n/I18nProvider.jsx";

const localeMap = {
  ru,
  en: enUS,
  uk,
  fr,
  uz,
};

/**
 * @callback FnFormat
 * @param {Date} date
 * @param {String} pattern
 * @returns {String}
 */

/**
 * Форматирует дату в зависимости от её расстояния от системной.
 * @param {Date} date дата, которую надо отобразить
 * @param {Locale} locale локаль
 */
function formatSmartDate(date, locale = localeMap[1]) {
  const now = new Date();

  // 1. Тот же день
  if (isSameDay(date, now)) {
    return format(date, "HH:mm", { locale });
  }

  // 2. Не дальше 7 дней (±) от текущей даты
  if (Math.abs(differenceInCalendarDays(now, date)) <= 7) {
    // 'EEEEEE' — краткий день недели («ср», «We»)
    return format(date, "EEEEEE HH:mm", { locale });
  }

  // 3. Тот же календарный год
  if (isSameYear(date, now)) {
    // 'd MMM' — «15 апр», «15 Apr»
    return format(date, "d MMM", { locale });
  }

  // 4. Другой год
  return format(date, "MMM yyyy", { locale }); // «апр 2023», «Apr 2023»
}

/**
 * Форматирует «последнее появление в сети»
 * @param {Date} date - Дата события
 * @param {function} translate - функция для текстовых переводов
 * @param {Locale} locale локаль
 * @returns {string}
 */
export function formatLastSeen(date, translate, locale = localeMap[1]) {
  const now = new Date();

  let patternKey;
  let formatted;

  if (isSameDay(date, now)) {
    patternKey = "time";
    formatted = format(date, "HH:mm", { locale });
  } else if (Math.abs(differenceInCalendarDays(now, date)) <= 7) {
    patternKey = "weekday";
    formatted = format(date, "EEEEEE HH:mm", { locale }); // «ср 13:45», «We 13:45»
  } else if (isSameYear(date, now)) {
    patternKey = "dayMonth";
    formatted = format(date, "d MMM", { locale }); // «15 апр», «15 Mar»
  } else {
    patternKey = "monthYear";
    formatted = format(date, "MMM yyyy", { locale }); // «апр 2023», «Apr 2023»
  }

  return translate(`chat.lastOnline.${patternKey}`, { date: formatted });
}

/**
 * @returns {{
 * format: FnFormat,
 * formatSmartDate: function(date: Date),
 * formatLastSeen: function(date: Date, translate: function)
 * }}
 */
const useDateUtils = () => {
  const { locale } = useLocale();

  return {
    /** @type {FnFormat} */
    format: (date, pattern) => format(date, pattern, { locale: localeMap[locale] }),
    formatSmartDate: (date) => formatSmartDate(date, localeMap[locale]),
    formatLastSeen: (date, translate) => formatLastSeen(date, translate, localeMap[locale]),
  };
};

export default useDateUtils;
